import * as React from 'react'

import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import useSensors from '~/client/src/shared/hooks/useSensors'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  isShown: boolean
  projectId: string
  onClose: () => void
}

const StruxTagCreateDialog: React.FC<IProps> = ({
  isShown,
  projectId,
  onClose,
}) => {
  const [infuseId, setInfuseId] = React.useState('')
  const [name, setName] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)

  const { save, schema } = useSensors({ projectId })
  const defaultTemplateId = schema.templates[0]?.id ?? null

  React.useEffect(() => {
    if (!isShown) {
      return
    }

    setInfuseId('')
    setName('')
    setError(null)
  }, [isShown])

  const handleClose = React.useCallback(() => {
    if (loading) {
      return
    }
    onClose()
  }, [loading, onClose])

  const handleSave = React.useCallback(async () => {
    const normalizedInfuseId = infuseId.trim()
    const normalizedName = name.trim()

    if (!normalizedInfuseId) {
      setError(Localization.translator.infuseIdIsRequired)
      return
    }

    if (normalizedInfuseId.includes(' ')) {
      setError(Localization.translator.infuseIdMustNotContainSpaces)
      return
    }

    setError(null)

    try {
      setLoading(true)
      await save(null, {
        infuseId: normalizedInfuseId,
        name: normalizedName || normalizedInfuseId,
        _templateId: defaultTemplateId,
      })

      setLoading(false)
      onClose()
    } catch {
      setLoading(false)
      setError(Localization.translator.failedToRegisterStruxTag)
    }
  }, [defaultTemplateId, infuseId, name, onClose, save])

  return (
    <Dialog
      open={isShown}
      onOpenChange={open => {
        if (!open) {
          handleClose()
        }
      }}
      disablePointerDismissal={loading}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{Localization.translator.registerStruxTag}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <StruxhubInput
            id="struxtag-create-infuse-id"
            label={Localization.translator.infuseId}
            value={infuseId}
            onChange={event => setInfuseId(event.target.value)}
            isRequiredTextHidden={true}
            isRequired={true}
          />
          <div className="tw:mt-2.5">
            <StruxhubInput
              id="struxtag-create-name"
              label={Localization.translator.name}
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder={Localization.translator.optional}
            />
          </div>
          {error && (
            <div className="tw:text-body-sm tw:text-destructive tw:mt-2.5">
              {error}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            size="lg"
            onClick={handleClose}
            disabled={loading}
          >
            {Localization.translator.cancel}
          </Button>
          <Button size="lg" onClick={handleSave} disabled={loading}>
            {loading && (
              <span data-icon="inline-start" className="tw:inline-flex">
                <Spinner size="sm" variant="white" />
              </span>
            )}
            {loading
              ? Localization.translator.registering
              : Localization.translator.register}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default observer(StruxTagCreateDialog)
